import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Menu, X, Sparkles, BookOpen, LayoutDashboard, Home, MessageCircle, LogIn, LogOut, User, Users } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV_ITEMS = [
  { path: "/", label: "Home", icon: Home },
  { path: "/library", label: "Library", icon: BookOpen },
  { path: "/forum", label: "Community", icon: Users },
  { path: "/guide", label: "Guide", icon: MessageCircle, auth: true },
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, auth: true },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();

  const filteredNav = NAV_ITEMS.filter((item) => !item.auth || user);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="main-header"
      className="sticky top-0 z-50 bg-[#F9F6F0]/80 backdrop-blur-xl border-b border-[#E3DDD2]/50"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2 transition-opacity duration-300 hover:opacity-80"
          >
            <Sparkles className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="font-heading text-xl font-medium text-[#1A221E] tracking-tight">
              Awakening Path
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? "bg-[#8FA69A] text-white"
                      : "text-[#737E79] hover:text-[#1A221E] hover:bg-[#EAE2D6]"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}

            {/* Auth section */}
            {!loading && (
              user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    data-testid="user-menu-trigger"
                    className="flex items-center gap-2 px-3 py-2 rounded-full text-sm text-[#737E79] hover:bg-[#EAE2D6] transition-all duration-300 ml-2"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#8FA69A] flex items-center justify-center">
                      <span className="text-xs font-medium text-white">{(user.name || user.email || "U")[0].toUpperCase()}</span>
                    </div>
                    <span className="hidden lg:inline">{user.name || user.email}</span>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-xl w-48" align="end">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium text-[#1A221E]">{user.name}</p>
                      <p className="text-xs text-[#737E79]">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="menu-dashboard"
                      onClick={() => navigate("/dashboard")}
                      className="cursor-pointer"
                    >
                      <LayoutDashboard className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="menu-guide"
                      onClick={() => navigate("/guide")}
                      className="cursor-pointer"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      AI Guide
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="logout-btn"
                      onClick={handleLogout}
                      className="cursor-pointer text-red-500"
                    >
                      <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-[#8FA69A] text-white hover:bg-[#2C4A3B] transition-all duration-300 ml-2"
                >
                  <LogIn className="w-4 h-4" strokeWidth={1.5} />
                  Sign In
                </Link>
              )
            )}
          </nav>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 rounded-full text-[#737E79] hover:bg-[#EAE2D6] transition-colors duration-300"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.5} /> : <Menu className="w-5 h-5" strokeWidth={1.5} />}
          </button>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <nav data-testid="mobile-nav" className="md:hidden pb-4 space-y-1">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                    isActive ? "bg-[#8FA69A] text-white" : "text-[#737E79] hover:text-[#1A221E] hover:bg-[#EAE2D6]"
                  }`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {item.label}
                </Link>
              );
            })}
            {!loading && (
              user ? (
                <button
                  data-testid="mobile-logout-btn"
                  onClick={() => { handleLogout(); setMobileOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-red-500 hover:bg-red-50 w-full text-left transition-all duration-300"
                >
                  <LogOut className="w-4 h-4" strokeWidth={1.5} />
                  Sign Out
                </button>
              ) : (
                <Link
                  to="/login"
                  data-testid="mobile-nav-login"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium bg-[#8FA69A] text-white"
                >
                  <LogIn className="w-4 h-4" strokeWidth={1.5} />
                  Sign In
                </Link>
              )
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
